import { createSelector } from '@reduxjs/toolkit';
import type { AutoConfiguratorState } from '@snap-types/models/auto-configurator/state';

type QuoteContextFallback = Partial<
  Pick<
    AutoConfiguratorState['quoteContext'],
    'snapQuoteUuid' | 'alphaQuoteUuid'
  >
>;

export type ResolvedQuoteContext = {
  snapQuoteUuid: string;
  alphaQuoteUuid: string;
};

export function selectAutoConfiguratorStore(state: AutoConfiguratorState) {
  return state;
}

export function selectIsRecalculating(state: AutoConfiguratorState) {
  return state.pendingRecalculationCount > 0;
}

/**
 * Selects the quote context for the Auto Configurator, which includes the
 * `snapQuoteUuid` and `alphaQuoteUuid`. If these values are not
 * present in the state, it will attempt to use the provided fallback values.
 * If neither the state nor the fallback provides both values, it returns null.
 *
 * @remarks
 *
 * The Auto Configurator slice is initialized before there is a quote context.
 * In <AutoConfigurator /> we dispatch an `initailize` action which sets them
 * in the store. This selector allows components to access the quote context
 * even before it has been initialized, as long as the necessary values are
 * provided via the fallback (e.g. from the URL).
 */
export const selectResolvedQuoteContext = createSelector(
  [
    (state: AutoConfiguratorState) => state.quoteContext.snapQuoteUuid,
    (state: AutoConfiguratorState) => state.quoteContext.alphaQuoteUuid,
    (_state: AutoConfiguratorState, fallback?: QuoteContextFallback) =>
      fallback?.snapQuoteUuid,
    (_state: AutoConfiguratorState, fallback?: QuoteContextFallback) =>
      fallback?.alphaQuoteUuid,
  ],
  (
    stateSnapQuoteUuid,
    stateAlphaQuoteUuid,
    fallbackSnapQuoteUuid,
    fallbackAlphaQuoteUuid,
  ) => {
    const snapQuoteUuid = stateSnapQuoteUuid ?? fallbackSnapQuoteUuid;
    const alphaQuoteUuid = stateAlphaQuoteUuid ?? fallbackAlphaQuoteUuid;

    if (!snapQuoteUuid || !alphaQuoteUuid) {
      return null;
    }

    return {
      snapQuoteUuid,
      alphaQuoteUuid,
    } satisfies ResolvedQuoteContext;
  },
);

export function selectCanDeleteLineItem(
  state: AutoConfiguratorState,
  lineItemUuid: string,
) {
  const lineItem = state.lineItems[lineItemUuid];

  if (!lineItem) {
    return false;
  }

  return !lineItem.isPending;
}

export function selectCanDeleteQuantityBreak(
  state: AutoConfiguratorState,
  lineItemUuid: string,
  quantityBreakUuid: string,
) {
  const lineItem = state.lineItems[lineItemUuid];
  const quantityBreak = state.quantityBreaks[quantityBreakUuid];

  if (!lineItem || !quantityBreak) {
    return false;
  }

  return !lineItem.isPending && !quantityBreak.isPending;
}
